/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI to display when an error occurs */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show a retry button (default: true) */
  showRetry?: boolean;
  /** Custom error message (default: "Something went wrong") */
  errorMessage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      const { showRetry = true, errorMessage = 'Something went wrong' } = this.props;

      return (
        <div className="flex flex-col items-center justify-center p-6 text-center" role="alert">
          <AlertTriangle className="h-10 w-10 text-destructive mb-4" aria-hidden="true" />
          <h3 className="text-lg font-semibold mb-2">{errorMessage}</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
          </p>
          {showRetry && (
            <Button variant="outline" onClick={this.handleRetry}>
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
              Try Again
            </Button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Modal-specific Error Boundary
 * Smaller, more compact error display suitable for modals
 */
export class ModalErrorBoundary extends Component<
  Omit<ErrorBoundaryProps, 'errorMessage'>,
  ErrorBoundaryState
> {
  constructor(props: Omit<ErrorBoundaryProps, 'errorMessage'>) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ModalErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { showRetry = true } = this.props;

      return (
        <div className="flex flex-col items-center justify-center p-4 text-center" role="alert">
          <AlertTriangle className="h-8 w-8 text-destructive mb-3" aria-hidden="true" />
          <p className="text-sm text-muted-foreground mb-3">
            {this.state.error?.message || 'An error occurred while loading this content.'}
          </p>
          {showRetry && (
            <Button variant="outline" size="sm" onClick={this.handleRetry}>
              <RefreshCw className="h-3 w-3 mr-1" aria-hidden="true" />
              Retry
            </Button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
